import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import queryString, { ParsedQuery } from 'query-string'
import {
  ComplianceFrameworkFilter,
  IsCustomizedFilter,
  ListPoliciesRequest,
  PolicySort,
} from '@trustero/trustero-api-web/lib/model/policy_pb'
import { LabelFilter } from '@trustero/trustero-api-web/lib/model/filters_pb'
import { MODEL_TYPE } from '@trustero/trustero-api-web/lib/common/model_pb'
import { NONE_ID } from 'src/Utils/globalConstants'
import { applyFrameworkFilter } from 'src/components/Reusable/IndexPage/FilterBar/FilterBar.utils'
import { DocumentationFilterValues } from 'src/components/Reusable/IndexPage/FilterBar/FilterBar.constants'
import { BoolValue } from 'google-protobuf/google/protobuf/wrappers_pb'
import { useScopedColumnHidden } from 'src/context/ColumnHideListContext'
import { INDEX_PAGE_WITH_FILTERS } from 'src/components/Reusable/IndexPage/IndexPage.constants'
import { GatekeeperFlag, IsGatekeeperEnabled } from 'src/context/Gatekeeper'
import { FilterParam } from '../../../components/Reusable/IndexPage/FilterBar/FilterBar.types'
import {
  GridColumnSortPolicies,
  GridColumnSortType,
  SORT_ORDER,
} from '../../../components/Reusable/Grid/GridColumnSort/GridColumnSort.constants'
import {
  Owner,
  COMPLIANCE_FRAMEWORK_MODEL_ID_PREFIX,
} from '../../../Utils/globalEnums'
import {
  queryParamAsArray,
  queryParamAsString,
} from '../../../Utils/queryParams'
import { POLICY_SORT_COLUMNS } from './PoliciesIndexPage.constants'

const applyFilter = (
  listPoliciesReq: ListPoliciesRequest,
  queryParams: ParsedQuery<string>,
  filterType: FilterParam,
) => {
  const filterParams = queryParamAsArray(queryParams[filterType])
  if (!filterParams?.length) {
    return
  }

  switch (filterType) {
    case FilterParam.DEPARTMENT: {
      const departmentIds = new Set(filterParams)
      listPoliciesReq.setDepartmentIdsList(Array.from(departmentIds))
      break
    }
    case FilterParam.LABEL: {
      const labelFilter = new LabelFilter()
      const labelIds = new Set(filterParams)
      if (labelIds.has(NONE_ID)) {
        labelIds.delete(NONE_ID)
        labelFilter.setIncludeNoLabel(new BoolValue().setValue(true))
      }
      labelFilter.setLabelIdsList(Array.from(labelIds))
      listPoliciesReq.setLabelFilter(labelFilter)
      break
    }
    case FilterParam.HAS_DOCUMENTATION: {
      if (
        filterParams.includes(DocumentationFilterValues.TRUE) &&
        filterParams.includes(DocumentationFilterValues.FALSE)
      )
        return
      const boolValue: boolean = JSON.parse(
        filterParams.filter(
          (value) =>
            value === DocumentationFilterValues.TRUE ||
            value === DocumentationFilterValues.FALSE,
        )[0],
      )
      listPoliciesReq.setHasDocumentation(new BoolValue().setValue(boolValue))
      break
    }
    case FilterParam.IS_CUSTOMIZED: {
      const isCustomizedOptions = new Set(filterParams)
      const isCustomizedFilter = new IsCustomizedFilter()
      if (isCustomizedOptions.has(DocumentationFilterValues.TRUE)) {
        isCustomizedFilter.setIncludeCustomized(true)
      }
      if (isCustomizedOptions.has(DocumentationFilterValues.FALSE)) {
        isCustomizedFilter.setIncludeNotCustomized(true)
      }
      listPoliciesReq.setIsCustomizedFilter(isCustomizedFilter)
      break
    }
    case FilterParam.OWNER: {
      const emails = [...filterParams]
      const idx = emails.findIndex(
        (email) => email?.toLowerCase() === Owner.UNASSIGNED_BE,
      )
      if (idx > -1) {
        emails.splice(idx, 1)
        listPoliciesReq.setOwnersList(emails)
        listPoliciesReq.setIncludeUnassigned(new BoolValue().setValue(true))
      } else {
        listPoliciesReq.setOwnersList(emails)
      }
      break
    }
    case FilterParam.ASSIGNEE: {
      const emails = [...filterParams]
      const idx = emails.findIndex(
        (email) => email?.toLowerCase() === Owner.UNASSIGNED_BE,
      )
      if (idx > -1) {
        emails.splice(idx, 1)
        listPoliciesReq.setAssigneeIncludeUnassigned(true)
      }
      listPoliciesReq.setAssigneeEmailsList(emails)
      break
    }
    case FilterParam.AUDIT:
      listPoliciesReq.setAuditIdsList(filterParams)
      break
    case FilterParam.COMPLIANCE_FRAMEWORK: {
      // A framework that also has a requirement subset selected is filtered by
      // those requirements via a PolicyFrameworkFilter
      const complianceFrameworkFilter = new ComplianceFrameworkFilter()
      const complianceFrameworkIds = new Set(
        filterParams.filter(
          (id) =>
            id === NONE_ID || queryParamAsArray(queryParams[id]).length === 0,
        ),
      )
      const includeNone = complianceFrameworkIds.delete(NONE_ID)
      if (includeNone) {
        complianceFrameworkFilter.setIncludeNone(new BoolValue().setValue(true))
      }
      if (!complianceFrameworkIds.size && !includeNone) {
        break
      }
      complianceFrameworkFilter.setComplianceFrameworkIdsList(
        Array.from(complianceFrameworkIds),
      )
      listPoliciesReq.setComplianceFrameworkFilter(complianceFrameworkFilter)
      break
    }
    case FilterParam.STATUS: {
      const complianceFrameworkFilter = new ComplianceFrameworkFilter()
      const complianceFrameworkIds = new Set(filterParams)
      if (complianceFrameworkIds.has(NONE_ID)) {
        complianceFrameworkIds.delete(NONE_ID)
        complianceFrameworkFilter.setIncludeNone(new BoolValue().setValue(true))
      }
      complianceFrameworkFilter.setComplianceFrameworkIdsList(
        Array.from(complianceFrameworkIds),
      )
      listPoliciesReq.setComplianceFrameworkFilter(complianceFrameworkFilter)
      break
    }
    default: {
      break
    }
  }
}

const applySort = (
  policiesRequest: ListPoliciesRequest,
  queryParams: ParsedQuery<string>,
) => {
  const sortBy =
    (queryParamAsString(queryParams.sort_by) as GridColumnSortType) ||
    GridColumnSortType.ASCENDING
  const sortCol =
    (queryParamAsString(queryParams.sort_col) as GridColumnSortPolicies) ||
    GridColumnSortPolicies.NAME

  const req = new PolicySort()
    .setSortOrder(SORT_ORDER[sortBy])
    .setSortColumn(POLICY_SORT_COLUMNS[sortCol])
  policiesRequest.setPolicySort(req)
}

/**
 * Every framework writes its selected requirement model_ids (or `none`) under a query
 * key equal to the framework's own model_id (prefix `trcf-`)
 */
const applyFrameworkRequirementFilters = (
  listPoliciesReq: ListPoliciesRequest,
  queryParams: ParsedQuery<string>,
): void => {
  Object.keys(queryParams).forEach((key) => {
    if (!key.startsWith(COMPLIANCE_FRAMEWORK_MODEL_ID_PREFIX)) return
    const requirementIds = queryParamAsArray(queryParams[key])
    if (!requirementIds.length) return
    applyFrameworkFilter(
      listPoliciesReq,
      key,
      requirementIds,
      MODEL_TYPE.POLICY,
    )
  })
}

/**
 * Adds Filter & Sorting information for Policies to pass in a gRPC request
 *
 * @param locSearch
 * @returns ListControlsRequest
 */
const getPoliciesGridFilterRequest = (
  locSearch: string,
): ListPoliciesRequest => {
  const queryParams: ParsedQuery<string> = queryString.parse(locSearch, {
    arrayFormat: 'bracket',
  })
  const dismissedValue = queryParamAsString(queryParams.dismissed)
  const listPoliciesReq: ListPoliciesRequest =
    new ListPoliciesRequest().setIsDismissed(
      new BoolValue().setValue(
        JSON.parse(
          dismissedValue &&
            (dismissedValue === 'false' || dismissedValue === 'true')
            ? dismissedValue
            : 'false',
        ),
      ),
    )
  Object.values(FilterParam).forEach((filterType) =>
    applyFilter(listPoliciesReq, queryParams, filterType),
  )
  applyFrameworkRequirementFilters(listPoliciesReq, queryParams)
  applySort(listPoliciesReq, queryParams)
  return listPoliciesReq
}

export const usePolicyRequest = (): ListPoliciesRequest => {
  const location = useLocation()
  return useMemo(
    () => getPoliciesGridFilterRequest(location.search),
    [location.search],
  )
}

export interface PoliciesColumnsVisibility {
  showDepartment: boolean
  showName: boolean
  // The policy's record in the GRC platform it was imported from.
  showSource: boolean
  showLabels: boolean
  showFrameworks: boolean
  showDocumentation: boolean
  showLastReviewed: boolean
  showLastApproved: boolean
  showControls: boolean
  showOwner: boolean
  showAssignees: boolean
}

export const usePoliciesColumnsVisibility = (): PoliciesColumnsVisibility => {
  const isHidden = useScopedColumnHidden(INDEX_PAGE_WITH_FILTERS.POLICIES)
  const showLabels = IsGatekeeperEnabled(GatekeeperFlag.LABELS)

  return {
    showDepartment: !isHidden('department'),
    showName: true,
    showSource: !isHidden('source'),
    showLabels: showLabels && !isHidden('labels'),
    showFrameworks: !isHidden('frameworks'),
    showDocumentation: !isHidden('documentation'),
    showLastReviewed: !isHidden('lastReviewed'),
    showLastApproved: !isHidden('lastApproved'),
    showControls: !isHidden('controls'),
    showOwner: !isHidden('owner'),
    showAssignees: !isHidden('assignees'),
  }
}

export const getPoliciesGridTemplateColumns = (
  showContentMatchColumn: boolean,
  visibility: PoliciesColumnsVisibility,
): string => {
  const tracks: string[] = []
  if (showContentMatchColumn) tracks.push('min-content')
  if (visibility.showDepartment) tracks.push('min-content')
  if (visibility.showName) tracks.push('minmax(min-content, 1fr)')
  if (visibility.showSource) tracks.push('auto')
  if (visibility.showLabels) tracks.push('auto')
  if (visibility.showFrameworks) tracks.push('auto')
  if (visibility.showDocumentation) tracks.push('auto')
  if (visibility.showLastReviewed) tracks.push('auto')
  if (visibility.showLastApproved) tracks.push('auto')
  if (visibility.showControls) tracks.push('auto')
  if (visibility.showOwner) tracks.push('auto')
  if (visibility.showAssignees) tracks.push('auto')
  tracks.push('auto')
  return tracks.join(' ')
}
